import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Badge,
  Tooltip,
  Fab,
  Divider,
  Popover,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { motion } from "framer-motion";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import ProfilePage from "./ProfilePage";
import HomeFeed from "../components/HomeFeed";
import NotificationsPage from "./NotificationsPage";
import QuickMomentPage from "./QuickMomentPage";

function DashboardPage() {
  const navigate = useNavigate();

  const [activeView, setActiveView] = useState("home");
  const [currentUser, setCurrentUser] = useState(null);

  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSession, setActiveSession] = useState(null);

  // ✅ Always derive IDs safely (no crash when currentUser is null)
  const currentUserId = useMemo(() => currentUser?.id ?? null, [currentUser]);

  // ✅ SAFE localStorage access
  useEffect(() => {
    try {
      const savedView = localStorage.getItem("activeView");
      const rawUser = localStorage.getItem("user");
      const user = rawUser ? JSON.parse(rawUser) : null;

      if (!user) {
        navigate("/login", { replace: true });
        return;
      }

      setCurrentUser(user);
      if (savedView) setActiveView(savedView);
    } catch (err) {
      console.error("Storage error:", err);
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // -----------------------------
  // 🔔 Notifications
  // -----------------------------
  const fetchNotifications = useCallback(async () => {
    if (!currentUserId) return; // ✅ guard
    try {
      const res = await axios.get(
        `http://localhost:5000/notifications/${currentUserId}`
      );
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error("Notification error:", err);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) return; // ✅ guard

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [currentUserId, fetchNotifications]);

  const totalUnread = notifications.filter((n) => !n.isRead).length;

  // -----------------------------
  // ⚡ Active Moment (Uber-style)
  // -----------------------------
  const fetchActiveSession = useCallback(async () => {
    if (!currentUserId) return; // ✅ guard
    try {
      const res = await axios.get(
        `http://localhost:5000/connect/active/${currentUserId}`
      );
      setActiveSession(res.data.session || null);
    } catch (err) {
      console.error("Active moment error:", err);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) return; // ✅ guard

    fetchActiveSession();
    const interval = setInterval(fetchActiveSession, 15000);
    return () => clearInterval(interval);
  }, [currentUserId, fetchActiveSession]);

  // -----------------------------
  // 📬 Popover
  // -----------------------------
  const handleBellClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  // -----------------------------
  // 🔁 View Renderer
  // -----------------------------
  const renderView = () => {
    if (!currentUser) return null; // ✅ safe (not before hooks)

    switch (activeView) {
      case "home":
        return <HomeFeed userId={currentUser.id} />;

      case "quickmoment":
        return <QuickMomentPage />;

      case "profile":
        return <ProfilePage />;

      case "notifications":
        return <NotificationsPage />;

      default:
        return <HomeFeed userId={currentUser.id} />;
    }
  };

  // ✅ Instead of returning early before hooks, render a loading UI here
  if (!currentUser) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#F9FAFB",
        }}
      >
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100vh", backgroundColor: "#F9FAFB" }}>
      {/* ===== SIDEBAR ===== */}
      <motion.div
        animate={{ width: collapsed ? 0 : 260 }}
        transition={{ duration: 0.3 }}
        style={{
          backgroundColor: "#FFFFFF",
          borderRight: "1px solid #E5E7EB",
          boxShadow: "2px 0 8px rgba(0,0,0,0.04)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* HEADER */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Tooltip title={collapsed ? "Expand" : "Collapse"}>
              <IconButton onClick={() => setCollapsed(!collapsed)} color="primary">
                {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
              </IconButton>
            </Tooltip>

            <IconButton color="primary" onClick={handleBellClick}>
              <Badge
                color="error"
                badgeContent={totalUnread}
                invisible={totalUnread === 0}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Box>

          {/* NOTIFICATIONS POPOVER */}
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            PaperProps={{
              sx: {
                width: 300,
                maxHeight: 350,
                p: 1,
                borderRadius: 2,
                boxShadow: 4,
              },
            }}
          >
            <Typography sx={{ px: 2, py: 1, fontWeight: 600 }}>
              Notifications
            </Typography>
            <Divider />
            {notifications.length === 0 ? (
              <Typography sx={{ p: 2 }} color="text.secondary">
                No notifications yet 🎉
              </Typography>
            ) : (
              <List dense>
                {notifications.map((n) => (
                  <ListItem key={n.id} divider>
                    <ListItemText
                      primary={n.message || "New notification"}
                      secondary={new Date(n.createdAt).toLocaleString()}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Popover>

          <Divider sx={{ my: 2 }} />

          {/* MENU */}
          {[
            { id: "home", label: "🏠 Explore" },
            { id: "post", label: "🎯 Shot" },
            { id: "quickmoment", label: "⚡ Quick Moment" },
            { id: "profile", label: "🎫 Profile" },
          ].map((btn) => (
            <Button
              key={btn.id}
              fullWidth
              sx={{ mb: 1 }}
              variant={activeView === btn.id ? "contained" : "outlined"}
              onClick={() => {
                if (btn.id === "post") {
                  navigate("/post");
                  return;
                }
                setActiveView(btn.id);
                localStorage.setItem("activeView", btn.id);
              }}
            >
              {btn.label}
            </Button>
          ))}
        </Box>

        {/* LOGOUT */}
        <Box sx={{ p: 3 }}>
          <Button
            fullWidth
            color="error"
            variant="outlined"
            onClick={() => {
              localStorage.removeItem("user");
              navigate("/");
            }}
          >
            Logout
          </Button>
        </Box>
      </motion.div>

      {/* ===== RIGHT PANEL ===== */}
      <motion.div
        key={activeView}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "32px 48px",
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* ACTIVE MOMENT CARD */}
        {activeSession && (
          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: "18px",
              backgroundColor: "#DCFCE7",
              border: "2px solid #16A34A",
              cursor: "pointer",
            }}
            onClick={() => navigate(`/moment/${activeSession.id}`)}
          >
            <Typography fontWeight={700}>🔥 Active Moment</Typography>
            <Typography sx={{ mt: 1 }}>
              Connected with{" "}
              <strong>
                {activeSession.userAName === currentUser.username
                  ? activeSession.userBName
                  : activeSession.userAName}
              </strong>
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", mt: 1 }}>
              Expires at{" "}
              {new Date(activeSession.expiresAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          </Box>
        )}

        {/* MAIN CONTENT */}
        {renderView()}
      </motion.div>

      {/* COLLAPSE FAB */}
      {collapsed && (
        <Fab
          color="primary"
          size="small"
          onClick={() => setCollapsed(false)}
          sx={{ position: "absolute", top: 20, left: 20 }}
        >
          <MenuOpenIcon />
        </Fab>
      )}
    </Box>
  );
}

export default DashboardPage;
